// src/components/admin/AdminGuard.tsx
// ============================================================
// Summit League v2 — Admin Guard
//
// Role-check wrapper for admin routes. Checks AuthContext user
// role against a configurable minimum role level.
//
// Props:
//   requiredRole: 'admin' | 'moderator' (default: 'admin')
//     - 'admin': only admins pass
//     - 'moderator': admins AND moderators pass
//
// Behavior:
//   - Not authenticated → redirect to /auth
//   - Insufficient role → redirect to / with toast
// ============================================================

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';

interface AdminGuardProps {
  requiredRole?: 'admin' | 'moderator';
  children: React.ReactNode;
}

export default function AdminGuard({
  requiredRole = 'admin',
  children,
}: AdminGuardProps) {
  const { user, isAuthenticated, isLoading } = useAuthContext();

  // While auth state is loading, show nothing (prevents flash)
  if (isLoading) {
    return (
      <div className="sl-admin-loading">
        <div className="sl-admin-loading-spinner" />
      </div>
    );
  }

  // Not authenticated → login page
  if (!isAuthenticated || !user) {
    return <Navigate to="/auth" replace />;
  }

  // Check role hierarchy: admin passes all guards, moderator only passes 'moderator' guards
  const hasAccess =
    user.role === 'admin' ||
    (user.role === 'moderator' && requiredRole === 'moderator');

  if (!hasAccess) {
    // Redirect to dashboard — the toast will be shown by the page that checks
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}